import React from "react";
import { ClipboardList, Scale, Layers3, ListChecks, Info, PawPrint } from "lucide-react";

const FEATURES = [
  {
    id: "profile", icon: ClipboardList, title: "An investor profile, not a guess",
    description: "A 10-question profile captures investment objectives, time horizon, liquidity needs, financial reliance, risk tolerance, experience, and investment themes.",
    accent: "bg-[#EDE9FE] text-[#5B21B6]",
  },
  {
    id: "risk-logic", icon: Scale, title: "Rules-based risk assessment",
    description: "The engine scores risk tolerance and risk capacity, applies investment-objective limits, and flags conflicting answers for review before generating a model portfolio.",
    accent: "bg-[#DBEAFE] text-[#1E40AF]",
  },
  {
    id: "themes", icon: Layers3, title: "Theme-based model portfolios",
    description: "One or two selected investment themes are combined with a risk classification to determine model allocations across equities, bonds, and cash.",
    accent: "bg-[#D1FAE5] text-[#065F46]",
  },
  {
    id: "simulation", icon: ListChecks, title: "The allocation, explained",
    description: "The simulated-account experience displays model holdings, target weights, and the logic behind the allocation. Revised questionnaire answers can generate a revised model.",
    accent: "bg-[#FFEDD5] text-[#9A3412]",
  },
];

const THEMES = [
  "AI & Technology", "Healthcare & Life Sciences",
  "Energy & Infrastructure", "Consumer & Financial Economy",
];

const PawfolioFeatures = () => (
  <div className="grid sm:grid-cols-2 gap-x-8 gap-y-9">
    {FEATURES.map(({ id, icon: Icon, title, description, accent }, index) => (
      <article key={id} data-testid={`pawfolio-card-${id}`} className="border-t border-slate-200 pt-5">
        <div className="flex items-center justify-between mb-4">
          <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${accent}`}><Icon size={20} aria-hidden="true" /></span>
          <span className="text-xs font-semibold tabular-nums text-slate-400" aria-hidden="true">0{index + 1}</span>
        </div>
        <h3 data-testid={`pawfolio-feature-title-${id}`} className="font-display text-base font-bold text-slate-900">{title}</h3>
        <p data-testid={`pawfolio-feature-description-${id}`} className="mt-2 text-sm leading-relaxed text-slate-600">{description}</p>
      </article>
    ))}
  </div>
);

const PawfolioNotice = () => (
  <aside data-testid="pawfolio-notice-banner" aria-labelledby="pawfolio-notice-title" className="mt-10 rounded-2xl bg-slate-900 p-6 sm:p-8 text-white">
    <div className="flex items-center gap-2.5">
      <Info size={18} className="shrink-0 text-[#C4B5FD]" aria-hidden="true" />
      <h3 id="pawfolio-notice-title" data-testid="pawfolio-notice-title" className="text-base font-bold">About this overview</h3>
    </div>
    <div className="mt-4 grid md:grid-cols-2 gap-5 md:gap-10 text-sm leading-relaxed">
      <p data-testid="pawfolio-access-notice" className="text-slate-200">This website describes PawFolio only. It does not provide access to the questionnaire, portfolio recommendations, account opening, or investment operations. The current PawFolio implementation uses simulated balances and hypothetical holdings, not live trading or funded-account management.</p>
      <p data-testid="pawfolio-risk-disclosure" className="text-slate-200">Model outputs depend on the information supplied and do not address an investor's complete financial situation. Thematic portfolios may be concentrated. Investing involves risk, including loss of principal; no outcome is guaranteed. This overview does not represent SEC registration, approval, or endorsement.</p>
    </div>
  </aside>
);

export const PawfolioSection = () => (
  <section id="pawfolio" data-testid="pawfolio-section" aria-labelledby="pawfolio-heading" className="relative scroll-mt-20 border-y border-slate-200/70 bg-white py-20 md:py-24">
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-12 lg:gap-16">
        <div>
          <div data-testid="pawfolio-badge" className="inline-flex items-center gap-2 rounded-full border border-[#DDD6FE] bg-[#F5F3FF] px-3.5 py-1.5 text-xs font-semibold text-[#5B21B6]">
            <PawPrint size={14} aria-hidden="true" /> Product overview
          </div>
          <h2 id="pawfolio-heading" data-testid="pawfolio-heading" className="mt-6 text-base md:text-lg font-semibold text-slate-700">Robo-advisory services</h2>
          <p data-testid="pawfolio-name" className="font-display mt-2 text-4xl sm:text-5xl font-black tracking-tighter text-slate-900">Meet <span className="brand-gradient-text">PawFolio.</span></p>
          <p data-testid="pawfolio-description" className="mt-5 text-sm md:text-base leading-relaxed text-slate-600">FinX's digital investment advisory technology turns an investor questionnaire into an algorithm-generated model portfolio, risk classification, and target allocation — with the reasoning behind the mix.</p>
          <p data-testid="pawfolio-service-status" className="mt-5 text-xs font-semibold text-[#5B21B6]">Current scope: simulated-account framework</p>
          <div className="mt-8 border-t border-slate-200 pt-5">
            <h3 data-testid="pawfolio-themes-heading" className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Four investment themes</h3>
            <ul data-testid="pawfolio-themes" className="mt-3 flex flex-wrap gap-2">
              {THEMES.map((theme, index) => <li key={theme} data-testid={`pawfolio-theme-${index}`} className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2 text-xs font-medium text-slate-700">{theme}</li>)}
            </ul>
          </div>
        </div>
        <PawfolioFeatures />
      </div>
      <PawfolioNotice />
    </div>
  </section>
);
